import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs/esm';
import { cloneDeep } from 'lodash-es';
import { EMPTY, map, Observable, of, take } from 'rxjs';
import { DownloadService } from './download.service';

import {
  AgendaPoint,
  AnyResource,
  ApproverVoteGroup,
  ApproverVoteUser,
  Breadcrumb,
  Event,
  EventSubType,
  File,
  FileSubType,
  FinderDestination,
  PermissionLevel,
  ProcessType,
  ResourcePath,
  ResourceType,
  Room,
  RoomSubType,
  UserSettings,
} from '@models';
import {
  AccountService,
  AppEvent,
  AppEventType,
  EventQueueService,
  EventService,
  FileService,
  GroupService,
  ModalService,
  RoomService,
  UserBeanService,
  UtilitiesService,
  VoteService,
} from '@services';
import type { CopyableResource } from '@shared/components/resource-modal/copy-resource-modal.component';
import type { ResourceWithStatus } from '@shared/components/resource-modal/delete-resource-modal.component';
import type { SignatureItem } from '@shared/components/resource-modal/organization-signature-list-modal.component';
import {
  downloadFile,
  FileWithRelativePath,
  isEditable,
  isSet,
  logError,
} from '@shared/utils';
import {
  getDownloadMergeUrl,
  getDownloadSignedUrl,
  getDownloadUrl,
  getDownloadUrlWithAnnotations,
  getEventExcelExportUrl,
  getEventIcsExportUrl,
} from '@shared/utils/resource-url';
import { AppState } from '@store';

type DownloadType = 'annotated' | 'signed';

export interface NewResourceModalParams {
  scopeRid?: Nullable<number>;
  breadcrumb?: Nullable<Breadcrumb[]>;
  parentRid?: Nullable<number>;
  parentType?: Nullable<ResourceType>;
}

export interface NewEventModalParams extends NewResourceModalParams {
  initialDates?: Nullable<[Date, Date] | dayjs.Dayjs>;
  isMeeting?: boolean;
}

export interface NewFileModalParams extends NewResourceModalParams {
  skipLocationPicker?: boolean;
  files?: FileWithRelativePath[];
  processType?: Nullable<ProcessType>;
  eventFolder?: Nullable<boolean>;
}

export interface NewAgendaPointModalParams extends NewResourceModalParams {
  scopeRid: number;
  skipLocationPicker?: boolean;
}

const getPath = (url: string) => url.split(/[?;#]/)[0];

const isCurrentPath = (currentPath: string, newRid: number) =>
  getPath(currentPath) === `/rooms/${newRid}` ||
  getPath(currentPath) === `/events/${newRid}`;

type CopyDestination = Pick<
  FinderDestination,
  'rid' | 'type' | 'parentSubtype'
>;

type TemplateReference = { type: 'TEMPLATE'; rid: number; displayName: string };

@Service()
export class ResourceService {
  downloadService = inject(DownloadService);
  store = inject<Store<AppState>>(Store);
  router = inject(Router);
  modalService = inject(ModalService);
  eventService = inject(EventService);
  accountService = inject(AccountService);
  groupService = inject(GroupService);
  voteService = inject(VoteService);
  utilitiesService = inject(UtilitiesService);
  eventQueueService = inject(EventQueueService);
  fileService = inject(FileService);
  roomService = inject(RoomService);
  userBeanService = inject(UserBeanService);

  userBean!: UserSettings;

  constructor() {
    this.store
      .select((state) => state.user)
      .pipe(takeUntilDestroyed())
      .subscribe((user) => {
        this.userBean = user;
      });
  }

  processResourceDownload = (
    resource: AnyResource | AnyResource[],
    downloadType?: Nullable<DownloadType>
  ) => {
    if (Array.isArray(resource)) {
      const [onlyResource] = resource;

      if (resource.length === 1 && onlyResource?.type === ResourceType.FILE) {
        const urlDownload = getDownloadUrl(onlyResource.type, onlyResource.rid);

        downloadFile(urlDownload, onlyResource.displayName);
      }

      return;
    }

    const file = resource as File;

    if (
      file.secViewError === 4 &&
      resource.permission === PermissionLevel.SEC_PRINT
    ) {
      this.fileService.openProtectedFileDownloadDialog(file);
    } else if (resource.type === ResourceType.EVENT) {
      this.downloadRoom(resource);
    } else {
      let urlDownload;

      if (resource.type === ResourceType.FILE) {
        if (file.signedFile && downloadType === 'signed') {
          urlDownload = getDownloadSignedUrl(resource.type, resource.rid);
        } else if (file.annotations && downloadType === 'annotated') {
          urlDownload = getDownloadUrlWithAnnotations(
            resource.type,
            resource.rid
          );
        } else {
          urlDownload = getDownloadUrl(resource.type, resource.rid);
        }

        if (urlDownload) {
          downloadFile(urlDownload, resource.displayName);
        }
      } else if (resource.type === ResourceType.ROOM) {
        this.downloadService.downloadGenerated(
          this.utilitiesService.getDownloadZipUrl(resource.rid),
          resource.displayName
        );
      }
    }
  };

  downloadAntiVirusLimitExceededFile = (
    resource: File,
    downloadType?: Nullable<DownloadType>
  ) => {
    this.fileService
      .openDownloadAntiVirusLimitExceededFileModal(resource)
      .then((answer) => {
        if (answer) {
          this.processResourceDownload(resource, downloadType);
        }

        return answer;
      });
  };

  downloadLockedFile = (
    resource: File,
    downloadType?: Nullable<DownloadType>
  ) => {
    this.fileService.openDownloadLockedFileModal(resource).then((answer) => {
      if (answer) {
        if (resource.antiVirusLimitExceeded) {
          this.downloadAntiVirusLimitExceededFile(resource, downloadType);
        } else {
          this.processResourceDownload(resource, downloadType);
        }
      }

      return answer;
    });
  };

  downloadFile = (
    resource: AnyResource,
    downloadType?: Nullable<DownloadType>
  ) => {
    const fileResource = resource as AnyResource & {
      lockedById?: number;
      antiVirusLimitExceeded?: boolean;
    };

    if (
      resource.type === ResourceType.FILE &&
      fileResource.lockedById &&
      fileResource.lockedById > 0 &&
      fileResource.lockedById !== this.userBean.userId
    ) {
      this.downloadLockedFile(resource as File, downloadType);
    } else if (fileResource.antiVirusLimitExceeded) {
      this.downloadAntiVirusLimitExceededFile(resource as File, downloadType);
    } else {
      this.processResourceDownload(resource, downloadType);
    }
  };

  downloadRoom = (
    resources: AnyResource | AnyResource[],
    downloadType: Nullable<DownloadType> = null
  ) => {
    const resourcesArray = Array.isArray(resources) ? resources : [resources];
    const [firstResource] = resourcesArray;

    if (
      resourcesArray.length === 1 &&
      firstResource?.type === ResourceType.FILE
    ) {
      this.downloadFile(firstResource, downloadType);
    } else {
      this.modalService.downloadRoom(resourcesArray).then(() => {
        const urlDownload =
          this.utilitiesService.getDownloadZipUrl(resourcesArray);

        this.downloadService.downloadGenerated(
          urlDownload,
          firstResource?.displayName
        );
      });
    }
  };

  openCopyResourceModal = async (resources: CopyableResource[]) => {
    const { CopyResourceModalComponent } =
      await import('@shared/components/resource-modal/copy-resource-modal.component');

    return this.modalService.open(
      CopyResourceModalComponent,
      {
        resourcesToCopy: cloneDeep(resources),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  copyResource = (resource: AnyResource | AnyResource[]) => {
    const resourcesToCopy: AnyResource[] = Array.isArray(resource)
      ? resource
      : [resource];

    const [firstResource] = resourcesToCopy;

    if (
      resourcesToCopy.length === 1 &&
      firstResource?.type === ResourceType.EVENT
    ) {
      this.eventService.openCopyMeetingModal(firstResource as Event);
    } else {
      this.openCopyResourceModal(resourcesToCopy as CopyableResource[]).then(
        ({ finderDestination }) => {
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.RESET_ITEM_DETAIL)
          );

          this.getCopyDestinationPath(finderDestination)
            .pipe(take(1))
            .subscribe((path) => {
              if (getPath(this.router.url) === path) {
                this.modalService.closeAll();
                this.eventQueueService.dispatch(
                  new AppEvent(
                    AppEventType.REFRESH_EVENT_STRUCTURE,
                    finderDestination.rid
                  )
                );
                this.eventQueueService.dispatch(
                  new AppEvent(AppEventType.REFRESH_ROOM_LIST)
                );
              } else {
                this.router
                  .navigateByUrl(path)
                  .then(() => this.modalService.closeAll());
              }
            });
        }
      );
    }
  };

  getCopyDestinationPath = ({
    rid,
    type,
    parentSubtype,
  }: CopyDestination): Observable<string> => {
    if (rid === this.userBean.rootRoomsId) {
      return of('/rooms');
    }

    if (type === ResourceType.ROOM) {
      return parentSubtype === RoomSubType.ROOM_EVENT_FOLDER
        ? this.roomService.getRoom(rid).pipe(map(({ pid }) => `/events/${pid}`))
        : of(`/rooms/${rid}`);
    }

    if (type === ResourceType.EVENT) {
      return of(`/events/${rid}`);
    }

    if (type === ResourceType.AGENDA_POINT) {
      return this.eventService
        .getAgendaPointEvent(rid)
        .pipe(map((event) => `/events/${event.rid}`));
    }

    return EMPTY;
  };

  openMoveResourceModal = async (resources: AnyResource[]) => {
    const { MoveResourceModalComponent } =
      await import('@shared/components/resource-modal/move-resource-modal.component');

    return this.modalService.open(
      MoveResourceModalComponent,
      {
        resourceToMove: cloneDeep(resources),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  moveResource = (resource: AnyResource | AnyResource[]) => {
    const resourcesToMove: AnyResource[] = Array.isArray(resource)
      ? resource
      : [resource];

    if (isEditable(resourcesToMove)) {
      return this.openMoveResourceModal(resourcesToMove).then(
        ({ resources, finderDestination }) => {
          const [firstResource] = resources;

          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE)
          );

          if (isCurrentPath(this.router.url, firstResource.pid)) {
            this.eventQueueService.dispatch(
              new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE)
            );
            this.eventQueueService.dispatch(
              new AppEvent(AppEventType.REFRESH_ROOM_LIST)
            );
          } else {
            setTimeout(() => {
              if (
                firstResource.pid &&
                finderDestination &&
                finderDestination.type !== ResourceType.AGENDA_POINT
              ) {
                if (firstResource.pid === this.userBean.rootRoomsId) {
                  this.utilitiesService.redirectPage('/rooms', {
                    queryParamsHandling: null,
                  });
                } else {
                  if (
                    finderDestination.type === ResourceType.ROOM &&
                    [
                      RoomSubType.ROOM_GENERAL,
                      RoomSubType.ROOM_CONTEXT,
                    ].includes(finderDestination.parentSubtype)
                  ) {
                    this.utilitiesService.redirectPage(
                      `/rooms/${firstResource.pid}`,
                      {
                        queryParamsHandling: null,
                      }
                    );
                  } else if (
                    finderDestination.type === ResourceType.ROOM &&
                    finderDestination.parentSubtype ===
                      RoomSubType.ROOM_EVENT_FOLDER
                  ) {
                    this.roomService
                      .getRoom(finderDestination.rid)
                      .pipe(take(1))
                      .subscribe((pidObj) => {
                        this.utilitiesService.redirectPage(
                          `/events/${pidObj.pid}`,
                          {
                            queryParamsHandling: null,
                          }
                        );
                      });
                  } else if (finderDestination.type === ResourceType.EVENT) {
                    this.utilitiesService.redirectPage(
                      `/events/${finderDestination.rid}`,
                      {
                        queryParamsHandling: null,
                      }
                    );
                  } else if (
                    finderDestination.type === ResourceType.DISCUSSION
                  ) {
                    this.utilitiesService.redirectPage(
                      `/rooms/${finderDestination.rid}`,
                      {
                        queryParamsHandling: null,
                      }
                    );
                  }
                }
              } else if (
                finderDestination &&
                finderDestination.type === ResourceType.AGENDA_POINT
              ) {
                if (
                  isCurrentPath(
                    this.router.url,
                    firstResource.breadcrumb[2].rid
                  )
                ) {
                  this.eventQueueService.dispatch(
                    new AppEvent(
                      AppEventType.REFRESH_EVENT,
                      firstResource.breadcrumb[2].rid
                    )
                  );
                } else {
                  this.utilitiesService.redirectPage(
                    `/events/${firstResource.breadcrumb[2].rid}`,
                    {
                      queryParamsHandling: null,
                    }
                  );
                }
              }
            }, 100);
          }

          this.modalService.closeAll();

          return resources;
        }
      );
    }

    return Promise.resolve([]);
  };

  mergeResource = (resource: AnyResource) => {
    this.downloadService.downloadGenerated(
      getDownloadMergeUrl(resource),
      resource.displayName
    );
  };

  exportExcel = (resource: AnyResource) => {
    this.downloadService.downloadGenerated(
      getEventExcelExportUrl(resource.rid),
      resource.displayName
    );
  };

  exportIcs = (resource: AnyResource) => {
    this.downloadService.downloadGenerated(
      getEventIcsExportUrl(resource.rid),
      resource.displayName
    );
  };

  openNewEventModal = async ({
    scopeRid = null,
    breadcrumb = null,
    parentRid = null,
    parentType = null,
    initialDates = null,
    isMeeting = false,
  }: NewEventModalParams = {}) => {
    if (isMeeting) {
      const { NewMeetingModalComponent } =
        await import('@shared/components/resource-modal/new-meeting-modal.component');

      return this.modalService.open(
        NewMeetingModalComponent,
        {
          scopeRid,
          breadcrumb: cloneDeep(breadcrumb ?? []),
          parentRid,
          parentType,
          resourceType: ResourceType.EVENT,
          resourceSubType: EventSubType.MEETING,
          initialDates,
          skipFinder: parentRid !== null,
        },
        {
          modalDialogClass: 'lg-modal',
        }
      );
    }

    const { NewEventModalComponent } =
      await import('@shared/components/resource-modal/new-event-modal.component');

    return this.modalService.open(
      NewEventModalComponent,
      {
        scopeRid,
        breadcrumb: cloneDeep(breadcrumb ?? []),
        parentRid,
        parentType,
        resourceType: ResourceType.EVENT,
        resourceSubType: EventSubType.EVENT,
        initialDates,
        skipFinder: parentRid !== null,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  newEvent = (params: NewEventModalParams = {}) => {
    this.openNewEventModal(params).then((newResource) =>
      this.router.navigate([`/events/${newResource.rid}`])
    );
  };

  newUser = (subtype: number) =>
    this.accountService
      .userNewUser(subtype)
      .then((resource) =>
        resource.permission
          ? this.router.navigate([`/users/${resource.rid}`])
          : this.router.navigate(['/home'])
      );

  newGroup = (groupSubtype: number) => {
    this.groupService
      .create(groupSubtype)
      .then((resource) =>
        this.router.navigate([
          resource.permission ? `/groups/${resource.rid}` : '/home',
        ])
      );
  };

  openNewRoomModal = async ({
    scopeRid = null,
    breadcrumb = null,
    parentRid = null,
    parentType = null,
  }: NewResourceModalParams = {}) => {
    const { NewRoomModalComponent } =
      await import('@shared/components/resource-modal/new-room-modal.component');

    return this.modalService.open(
      NewRoomModalComponent,
      {
        scopeRid,
        breadcrumb: cloneDeep(breadcrumb ?? []),
        parentRid,
        parentType,
        resourceType: ResourceType.ROOM,
        resourceSubType: null,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  newRoom = (params: NewResourceModalParams = {}) => {
    return this.openNewRoomModal(params).then((resource) => {
      const { parentRid, parentType } = params;

      if (parentType === ResourceType.USER) {
        if (parentRid !== this.userBean.userId) {
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_USER)
          );
        } else {
          this.userBeanService.refreshUserBean();
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_TREE_LIST)
          );
          this.router.navigate([`/rooms/${resource.rid}`]);
        }
      } else {
        if (parentType !== ResourceType.EVENT && isSet(resource)) {
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_TREE_LIST)
          );
          this.router.navigate([`/rooms/${resource.rid}`]);
        } else {
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE, resource.rid)
          );
        }
      }
    });
  };

  openNewNoteModal = async ({
    scopeRid = null,
    breadcrumb = null,
    parentRid = null,
    parentType = null,
  }: NewResourceModalParams = {}) => {
    const { NewNoteModalComponent } =
      await import('@shared/components/resource-modal/new-note-modal.component');

    return this.modalService.open(
      NewNoteModalComponent,
      {
        scopeRid,
        breadcrumb: cloneDeep(breadcrumb ?? []),
        parentRid,
        parentType,
        resourceType: ResourceType.FILE,
        resourceSubType: FileSubType.FILE_NOTE,
        skipFinder: parentRid !== null,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  newNote = (params: NewResourceModalParams = {}) => {
    this.openNewNoteModal(params).then((resource) => {
      const { parentType } = params;

      this.eventQueueService.dispatch(
        new AppEvent(AppEventType.RESET_ROOM_LIST)
      );
      this.eventQueueService.dispatch(
        new AppEvent(AppEventType.OPEN_FILE_LINK, {
          rid: resource.rid,
        })
      );
      this.eventQueueService.dispatch(
        new AppEvent(AppEventType.REFRESH_ROOM_LIST)
      );

      if (
        parentType === ResourceType.EVENT ||
        parentType === ResourceType.AGENDA_POINT ||
        parentType === ResourceType.ROOM
      ) {
        this.eventQueueService.dispatch(
          new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE, resource.rid)
        );
      }
    });
  };

  newVote = (parentResource: Event | Room | AgendaPoint) => {
    this.voteService.newVote(parentResource).then((resource) => {
      this.eventQueueService.dispatch(
        new AppEvent(AppEventType.RESET_ROOM_LIST)
      );

      if (parentResource.type === ResourceType.EVENT) {
        this.eventQueueService.dispatch(
          new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE, resource.rid)
        );
      }
    });
  };

  newShortcut = (resource: File) => {
    this.fileService.newShortcut(resource).then((newResource) => {
      if (isCurrentPath(this.router.url, newResource.pid)) {
        this.eventQueueService.dispatch(
          new AppEvent(AppEventType.REFRESH_ROOM_LIST)
        );
      } else {
        if (newResource.pid) {
          if (newResource.pid === this.userBean.rootRoomsId) {
            this.router.navigate(['/rooms/']);
          } else {
            if (newResource.pType === ResourceType.ROOM) {
              this.router.navigate(['/rooms/' + newResource.pid]);
            } else if (newResource.pType === ResourceType.EVENT) {
              this.router.navigate(['/events/' + newResource.pid]);
            }
          }
        }
      }
    });
  };

  addShortcut = (resource: AnyResource, isAgendaPointChild: boolean) => {
    this.openAddShorcutModal(resource).then((newResource) => {
      if (
        isAgendaPointChild ||
        isCurrentPath(this.router.url, newResource.pid)
      ) {
        this.eventQueueService.dispatch(
          new AppEvent(AppEventType.REFRESH_ROOM_LIST)
        );
        this.eventQueueService.dispatch(
          new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE, newResource.rid)
        );
      } else {
        if (newResource.pid) {
          if (newResource.pid === this.userBean.rootRoomsId) {
            this.router.navigate(['/rooms/']);
          } else {
            this.router.navigate([`/rooms/${newResource.pid}`]);
          }
        } else {
          logError('Error creating a shortcut');
        }
      }
    });
  };

  openNewDiscussionModal = async ({
    scopeRid = null,
    breadcrumb = null,
    parentRid = null,
    parentType = null,
  }: NewResourceModalParams = {}) => {
    const { NewDiscussionModalComponent } =
      await import('@shared/components/resource-modal/new-discussion-modal.component');

    return this.modalService.open(
      NewDiscussionModalComponent,
      {
        scopeRid,
        breadcrumb: cloneDeep(breadcrumb ?? []),
        parentRid,
        parentType,
        resourceType: ResourceType.DISCUSSION,
        resourceSubType: null,
        skipFinder: parentRid !== null,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  newDiscussion = (params: NewResourceModalParams = {}) => {
    this.openNewDiscussionModal(params).then((newResource) => {
      this.eventQueueService.dispatch(
        new AppEvent(AppEventType.REFRESH_ITEMS_LIST, 'discussions')
      );
      this.eventQueueService.dispatch(
        new AppEvent(AppEventType.OPEN_DISCUSSION_LINK, newResource.rid)
      );
    });
  };

  openUploadFileModal = async ({
    scopeRid = null,
    breadcrumb = null,
    parentRid = null,
    parentType = null,
    skipLocationPicker = false,
    files = [],
    processType = null,
  }: NewFileModalParams = {}) => {
    const { NewFileModalComponent } =
      await import('@shared/components/resource-modal/new-resource/new-file-modal.component');

    return this.modalService.open(
      NewFileModalComponent,
      {
        scopeRid,
        breadcrumb: cloneDeep(breadcrumb ?? []),
        parentRid,
        parentType,
        skipLocationPicker,
        files,
        processType,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  uploadFile = (params: NewFileModalParams = {}) =>
    this.openUploadFileModal(params).then((responseArray) => {
      const { parentType, eventFolder } = params;

      if (responseArray.length === 1) {
        this.eventQueueService.dispatch(
          new AppEvent(AppEventType.REFRESH_ROOM_LIST)
        );

        if (
          parentType === ResourceType.EVENT ||
          parentType === ResourceType.AGENDA_POINT ||
          eventFolder
        ) {
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE)
          );
        }
      } else {
        if (
          parentType !== ResourceType.EVENT &&
          parentType !== ResourceType.AGENDA_POINT &&
          responseArray?.length
        ) {
          if (isCurrentPath(this.router.url, responseArray[0].pid)) {
            this.eventQueueService.dispatch(
              new AppEvent(AppEventType.REFRESH_ROOM_LIST)
            );
          } else {
            this.router.navigate(['/rooms', responseArray[0].pid]);
          }
        } else {
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE)
          );
        }
      }
    });

  newAgendaPoint = async ({
    scopeRid,
    breadcrumb = null,
    parentRid = null,
    parentType = null,
  }: NewAgendaPointModalParams) => {
    const { NewAgendaPointModalComponent } =
      await import('@shared/components/resource-modal/new-agenda-point-modal.component');

    return this.modalService.open(
      NewAgendaPointModalComponent,
      {
        scopeRid,
        breadcrumb: cloneDeep(breadcrumb ?? []),
        parentRid,
        parentType,
        resourceType: ResourceType.AGENDA_POINT,
        resourceSubType: null,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  newTask = async ({
    scopeRid = null,
    breadcrumb = null,
    parentRid = null,
    parentType = null,
  }: NewResourceModalParams = {}) => {
    const { NewTaskModalComponent } =
      await import('@shared/components/resource-modal/new-task-modal.component');

    return this.modalService.open(
      NewTaskModalComponent,
      {
        scopeRid,
        breadcrumb: cloneDeep(breadcrumb ?? []),
        parentRid,
        parentType,
        resourceType: ResourceType.ACTIVITY,
        resourceSubType: null,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  addOrganizationSignatureList = async (items: AnyResource[]) => {
    const { OrganizationSignatureListModalComponent } =
      await import('@shared/components/resource-modal/organization-signature-list-modal.component');

    return this.modalService.open(OrganizationSignatureListModalComponent, {
      items: cloneDeep(items) as SignatureItem[],
    });
  };

  viewApprovalVotes = async (file: File) => {
    const { ApprovalsLogModalComponent } =
      await import('@shared/components/resource-modal/approvals-log-modal.component');

    return this.modalService.open(
      ApprovalsLogModalComponent,
      {
        file: cloneDeep(file),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  openAddShorcutModal = async (parent: AnyResource) => {
    const { AddReferenceModalComponent } =
      await import('@shared/components/resource-modal/add-reference-modal.component');

    return this.modalService.open(
      AddReferenceModalComponent,
      {
        pidObj: parent,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  sendNotification = async (resource: AnyResource) => {
    const { NotificationModalComponent } =
      await import('@shared/components/resource-modal/notification-modal.component');

    return this.modalService.open(
      NotificationModalComponent,
      {
        resource: cloneDeep(resource),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  deleteResources = async (
    resources: (AnyResource | TemplateReference)[],
    type: Nullable<ResourcePath>
  ) => {
    const { DeleteResourceModalComponent } =
      await import('@shared/components/resource-modal/delete-resource-modal.component');

    return this.modalService.open(
      DeleteResourceModalComponent,
      {
        resources: cloneDeep(resources) as ResourceWithStatus[],
        type,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  guestAccess = async (resource: AnyResource) => {
    const { GuestAccessModalComponent } =
      await import('@shared/components/resource-modal/guest-access-modal.component');

    return this.modalService.open(
      GuestAccessModalComponent,
      {
        targetResource: cloneDeep(resource),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  confirmEditPermissions = async () => {
    const { ConfirmPermissionsModalComponent } =
      await import('@shared/components/resource-modal/confirm-permissions-modal.component');

    return this.modalService.open(
      ConfirmPermissionsModalComponent,
      {},
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  editResourceNotificationSelections = async (
    resource: AnyResource,
    rid?: number
  ) => {
    const { EditNotificationsModalComponent } =
      await import('@shared/components/resource-modal/edit-notifications-modal.component');

    return this.modalService.open(
      EditNotificationsModalComponent,
      {
        resource: cloneDeep(resource),
        rid,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  editResourceApprovalSelections = async (
    resource: File,
    votes: (ApproverVoteUser | ApproverVoteGroup)[]
  ) => {
    const { EditApprovalsModalComponent } =
      await import('@shared/components/resource-modal/edit-approvals-modal.component');

    return this.modalService.open(
      EditApprovalsModalComponent,
      {
        resource: cloneDeep(resource),
        votes: cloneDeep(votes),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };
}
